// Pure, dependency-free helpers for browser calculator/test tools.
// Matrix math (add/subtract/multiply/transpose/determinant/inverse) and
// date math (difference breakdown + add/subtract days). No DOM, no time,
// no randomness — safe to import on the server.

export type Matrix = number[][];

export type MatrixResult = { ok: true; matrix: Matrix } | { ok: false; error: string };

export type ScalarResult = { ok: true; value: number } | { ok: false; error: string };

const EPSILON = 1e-10;

/** Build a rows×cols grid of empty strings for editable inputs. */
export function makeEmptyGrid(rows: number, cols: number): string[][] {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''));
}

/**
 * Resize a string grid to the requested dimensions, preserving overlapping
 * cells and filling new cells with an empty string.
 */
export function resizeGrid(grid: string[][], rows: number, cols: number): string[][] {
  return Array.from({ length: rows }, (_unusedRow, r) =>
    Array.from({ length: cols }, (_unusedCol, c) => grid[r]?.[c] ?? ''),
  );
}

/** Parse a string grid into numbers. Empty cells count as 0. */
export function parseGrid(grid: string[][]): MatrixResult {
  const matrix: Matrix = [];
  for (let r = 0; r < grid.length; r += 1) {
    const row: number[] = [];
    for (let c = 0; c < grid[r].length; c += 1) {
      const raw = (grid[r][c] ?? '').trim();
      if (raw === '') {
        row.push(0);
        continue;
      }
      const value = Number(raw);
      if (!Number.isFinite(value)) {
        return { ok: false, error: `Cell R${r + 1}C${c + 1} is not a valid number.` };
      }
      row.push(value);
    }
    matrix.push(row);
  }
  return { ok: true, matrix };
}

export function addMatrices(a: Matrix, b: Matrix): MatrixResult {
  if (a.length !== b.length || a[0].length !== b[0].length) {
    return {
      ok: false,
      error: 'Addition needs both matrices to share the same rows and columns.',
    };
  }
  const matrix = a.map((row, r) => row.map((value, c) => value + b[r][c]));
  return { ok: true, matrix };
}

export function subtractMatrices(a: Matrix, b: Matrix): MatrixResult {
  if (a.length !== b.length || a[0].length !== b[0].length) {
    return {
      ok: false,
      error: 'Subtraction needs both matrices to share the same rows and columns.',
    };
  }
  const matrix = a.map((row, r) => row.map((value, c) => value - b[r][c]));
  return { ok: true, matrix };
}

export function multiplyMatrices(a: Matrix, b: Matrix): MatrixResult {
  if (a[0].length !== b.length) {
    return {
      ok: false,
      error: 'For A × B the columns of A must equal the rows of B.',
    };
  }
  const rows = a.length;
  const cols = b[0].length;
  const inner = b.length;
  const matrix: Matrix = Array.from({ length: rows }, () => Array.from({ length: cols }, () => 0));
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      let sum = 0;
      for (let k = 0; k < inner; k += 1) {
        sum += a[r][k] * b[k][c];
      }
      matrix[r][c] = sum;
    }
  }
  return { ok: true, matrix };
}

export function transposeMatrix(a: Matrix): MatrixResult {
  const rows = a.length;
  const cols = a[0].length;
  const matrix: Matrix = Array.from({ length: cols }, () => Array.from({ length: rows }, () => 0));
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      matrix[c][r] = a[r][c];
    }
  }
  return { ok: true, matrix };
}

export function determinant(a: Matrix): ScalarResult {
  const n = a.length;
  if (n === 0 || a[0].length !== n) {
    return { ok: false, error: 'The determinant is only defined for a square matrix.' };
  }
  // Gaussian elimination with partial pivoting.
  const m = a.map((row) => row.slice());
  let det = 1;
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(m[row][col]) > Math.abs(m[pivot][col])) {
        pivot = row;
      }
    }
    if (Math.abs(m[pivot][col]) < EPSILON) {
      return { ok: true, value: 0 };
    }
    if (pivot !== col) {
      const tmp = m[pivot];
      m[pivot] = m[col];
      m[col] = tmp;
      det = -det;
    }
    det *= m[col][col];
    for (let row = col + 1; row < n; row += 1) {
      const factor = m[row][col] / m[col][col];
      for (let k = col; k < n; k += 1) {
        m[row][k] -= factor * m[col][k];
      }
    }
  }
  return { ok: true, value: det };
}

export function inverseMatrix(a: Matrix): MatrixResult {
  const n = a.length;
  if (n === 0 || a[0].length !== n) {
    return { ok: false, error: 'Only a square matrix can be inverted.' };
  }
  // Gauss-Jordan elimination on [A | I].
  const m = a.map((row, r) =>
    row.concat(Array.from({ length: n }, (_unused, c) => (c === r ? 1 : 0))),
  );
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(m[row][col]) > Math.abs(m[pivot][col])) {
        pivot = row;
      }
    }
    if (Math.abs(m[pivot][col]) < EPSILON) {
      return {
        ok: false,
        error: 'This matrix is singular (determinant 0), so it has no inverse.',
      };
    }
    if (pivot !== col) {
      const tmp = m[pivot];
      m[pivot] = m[col];
      m[col] = tmp;
    }
    const pivotValue = m[col][col];
    for (let k = 0; k < 2 * n; k += 1) {
      m[col][k] /= pivotValue;
    }
    for (let row = 0; row < n; row += 1) {
      if (row === col) {
        continue;
      }
      const factor = m[row][col];
      if (factor === 0) {
        continue;
      }
      for (let k = 0; k < 2 * n; k += 1) {
        m[row][k] -= factor * m[col][k];
      }
    }
  }
  const matrix = m.map((row) => row.slice(n).map((value) => (Object.is(value, -0) ? 0 : value)));
  return { ok: true, matrix };
}

/** Round to at most 4 decimals and drop trailing zeros for display. */
export function formatCell(value: number): string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  const rounded = Math.round(value * 10000) / 10000;
  const normalized = Object.is(rounded, -0) ? 0 : rounded;
  return String(normalized);
}

// ---------------------------------------------------------------------------
// Date math
// ---------------------------------------------------------------------------

export type DateBreakdown = {
  totalDays: number;
  years: number;
  months: number;
  days: number;
  weeks: number;
  remainderDays: number;
  totalWeeks: number;
  totalHours: number;
  totalMinutes: number;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Parse a yyyy-mm-dd input into a UTC date, or null when invalid. */
export function parseDateInput(value: string): Date | null {
  if (!value) {
    return null;
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Difference between two dates. `inclusive` adds one day so both endpoints
 * count. Order does not matter — the absolute span is returned.
 */
export function diffDates(start: Date, end: Date, inclusive: boolean): DateBreakdown {
  const early = start.getTime() <= end.getTime() ? start : end;
  const late = start.getTime() <= end.getTime() ? end : start;

  let totalDays = Math.round((late.getTime() - early.getTime()) / MS_PER_DAY);
  if (inclusive) {
    totalDays += 1;
  }

  // Calendar-accurate years/months/days from the earlier to the later date.
  let years = late.getUTCFullYear() - early.getUTCFullYear();
  let months = late.getUTCMonth() - early.getUTCMonth();
  let days = late.getUTCDate() - early.getUTCDate();
  if (days < 0) {
    months -= 1;
    const daysInPrevMonth = new Date(
      Date.UTC(late.getUTCFullYear(), late.getUTCMonth(), 0),
    ).getUTCDate();
    days += daysInPrevMonth;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (inclusive) {
    days += 1;
  }

  return {
    totalDays,
    years,
    months,
    days,
    weeks: Math.floor(totalDays / 7),
    remainderDays: totalDays % 7,
    totalWeeks: Math.round((totalDays / 7) * 100) / 100,
    totalHours: totalDays * 24,
    totalMinutes: totalDays * 24 * 60,
  };
}

/** Add (or subtract, with a negative amount) whole days to a date. */
export function shiftDate(date: Date, amount: number): Date {
  return new Date(date.getTime() + amount * MS_PER_DAY);
}

/** Format a UTC date as yyyy-mm-dd. */
export function formatDateIso(date: Date): string {
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Human-readable date like "Monday, 14 July 2025". */
export function formatDateLong(date: Date): string {
  const weekday = WEEKDAYS[date.getUTCDay()];
  const month = MONTHS[date.getUTCMonth()];
  return `${weekday}, ${date.getUTCDate()} ${month} ${date.getUTCFullYear()}`;
}

// ---------------------------------------------------------------------------
// Typing test text samples + metrics (pure)
// ---------------------------------------------------------------------------

export const TYPING_SAMPLES: string[] = [
  'The quick brown fox jumps over the lazy dog while the sun sets slowly behind the quiet hills and a gentle breeze carries the smell of rain across the open field.',
  'Good typing is less about speed and more about rhythm. Keep your wrists relaxed, let your eyes read ahead, and trust your fingers to find the keys without looking down.',
  'A small stream of clear water moved between the stones, catching the light in bright flashes as birds called from the trees and the morning grew warmer by the minute.',
  'Every great project starts with a single line. Write it, test it, and improve it one careful step at a time until the whole thing works the way you first imagined.',
];

export type TypingMetrics = {
  correctChars: number;
  incorrectChars: number;
  typedChars: number;
  accuracy: number;
  grossWpm: number;
  netWpm: number;
};

/**
 * Compute typing metrics from the target text, what the user typed so far, and
 * the elapsed milliseconds. Standard WPM uses 5 characters per "word".
 */
export function computeTypingMetrics(
  target: string,
  typed: string,
  elapsedMs: number,
): TypingMetrics {
  let correctChars = 0;
  let incorrectChars = 0;
  for (let i = 0; i < typed.length; i += 1) {
    if (i < target.length && typed[i] === target[i]) {
      correctChars += 1;
    } else {
      incorrectChars += 1;
    }
  }
  const typedChars = typed.length;
  const minutes = elapsedMs > 0 ? elapsedMs / 60000 : 0;
  const accuracy = typedChars > 0 ? (correctChars / typedChars) * 100 : 100;
  const grossWpm = minutes > 0 ? typedChars / 5 / minutes : 0;
  const netWpm = minutes > 0 ? correctChars / 5 / minutes : 0;
  return {
    correctChars,
    incorrectChars,
    typedChars,
    accuracy: Math.round(accuracy * 10) / 10,
    grossWpm: Math.round(grossWpm),
    netWpm: Math.round(netWpm),
  };
}
